// src/services/clipsFeed.js
// MyClips Faz 1A Görev 6: Feed sorgusu için ortak helper'lar.
// Faz 1B Görev 4: rankBy parametresi — engagementScore desc sıralama opsiyonu.
//
// Amaç:
//   - Feed'de SADECE status == "ready" olan clipleri göster (işlenen/başarısız
//     olanlar feed'den gelmesin — kırık player önleyici).
//   - Cursor pagination (startAfter) ile "Daha fazla" akışı.
//   - Firestore `in` operatörü limit 30 → authorIds 30 ile cap'leniyor.
//   - rankBy="tarih" (varsayılan, kronolojik) | "engagementScore" (ranking).
//
// Firestore index'leri (bkz. firestore.indexes.json):
//   - clips: (authorId ASC, status ASC, tarih DESC)
//   - clips: (status ASC, tarih DESC)
//   - clips: (authorId ASC, status ASC, engagementScore DESC, tarih DESC)  ← Faz 1B
//   - clips: (status ASC, engagementScore DESC, tarih DESC)                 ← Faz 1B
//
// ⚠️ ENGAGEMENT RANK GOTCHA:
//   Firestore orderBy kullanılan alanı olmayan doc'ları dışlar. Yani
//   rankBy="engagementScore" açıldığında yalnızca rerank olmuş klipler
//   döner. Admin önce rerankClipsCallable ile mevcut kliplere skoru
//   yazmalı, sonra flag'i açmalı.

import {
  collection,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  getDocs,
  QueryConstraint,
} from "firebase/firestore";
import type { DocumentData, DocumentSnapshot, QueryDocumentSnapshot } from "firebase/firestore";
import { db } from "../firebase";

const CLIPS_COLL = "clips";

// Firestore v10 — `in` operatörünün max eleman sayısı 30.
export const IN_OPERATOR_MAX = 30;

export const DEFAULT_CLIPS_PAGE_SIZE = 10;

// Kabul edilen sıralama alanları. Yeni değer eklenirse composite index
// de eklenmeli.
const ALLOWED_RANK_BY = new Set(["tarih", "engagementScore"]);
const DEFAULT_RANK_BY = "tarih";

/**
 * Sıralama alanı. `string` — union DEĞİL, çünkü değer feature-flag'ten
 * (`getClipsFeedRankBy()`) geliyor ve doğrulanmıyor; `normalizeRankBy` zaten
 * bilinmeyeni varsayılana düşürüyor. Union yazmak veriyi yalanlardı.
 */
type SiralamaAlani = string | null | undefined;

/** Cursor — `getDocs` sonucu ya da hiç. */
type Imlec = DocumentSnapshot | QueryDocumentSnapshot | null | undefined;

/**
 * Klip sayfa seçenekleri — DÖRT fonksiyonun ORTAK girdisi.
 * Eskiden bu şekil dört ayrı JSDoc `@param` satırında elle yazılıydı ve
 * derlenmiyordu. Bu turda `useSpotlight`ta aynı sınıfın ayrışmış hâlini
 * ölçtük; burada kopyalar HENÜZ ayrışmamışken tekilleştiriliyor.
 */
/**
 * Sayfa ögesi — `{ ...d.data(), id, type: "clip" }`. Ham Firestore belgesi
 * olduğu için taban `DocumentData`; kesin olan iki alan yazılı.
 */
export type KlipOgesi = DocumentData & { id: string; type: string };

export type KlipSayfaSecenekleri = {
  authorIds?: string[] | null;
  pageSize?: number;
  cursorDoc?: Imlec;
  rankBy?: SiralamaAlani;
};

function normalizeRankBy(rankBy?: SiralamaAlani) {
  const v = String(rankBy || DEFAULT_RANK_BY);
  return ALLOWED_RANK_BY.has(v) ? v : DEFAULT_RANK_BY;
}

// rankBy'ye göre orderBy zinciri üret. Ranking modunda tie-breaker olarak
// tarih desc eklenir → aynı skora sahip klipler yeniden yayında üstte kalır.
function orderByForRank(rankBy?: SiralamaAlani) {
  const r = normalizeRankBy(rankBy);
  if (r === "engagementScore") {
    return [orderBy("engagementScore", "desc"), orderBy("tarih", "desc")];
  }
  return [orderBy("tarih", "desc")];
}

/**
 * Takip edilen yazarlar için ready-only clip query'si üretir.
 * `authorIds` boşsa `null`. (Girdi şekli → `KlipSayfaSecenekleri`.)
 */
export function buildReadyClipsForAuthorsQuery({
  authorIds,
  pageSize = DEFAULT_CLIPS_PAGE_SIZE,
  cursorDoc = null,
  rankBy = DEFAULT_RANK_BY,
}: KlipSayfaSecenekleri) {
  const ids = Array.isArray(authorIds)
    ? authorIds.filter(Boolean).slice(0, IN_OPERATOR_MAX)
    : [];
  if (ids.length === 0) return null;

  // EMİR #99 §4.2 — V3 SÖZLEŞMESİ (diğer 13 yüzeyle AYNI).
  //
  // ⚠️ ESKİ HÂLİ: `where("status","in",[READY, PROCESSING])`. Bugünkü yükleyici
  // (`myclipsv3/compose/pipeline/firestoreWrite.ts:119`) `status:"v3_only"`
  // yazıyor → 128/141 klip sorgunun KAPSAMI DIŞINDAYDI. Aynı sınıf profil
  // yüzeyinde bulunup düzeltilmiş (`UserPosts.tsx:19-26`), ana akış atlanmıştı.
  //
  // ⚠️ `status` filtresi KALDIRILDI, `v3_ready` ile DEĞİŞTİRİLMEDİ diye
  // okunmasın: ikisi ayrı eksen. `status` artık ayırt edici değil (canlı değer
  // şema sabitinde yok); yayınlanmışlık sinyali `v3_ready`.
  //
  // 🔒 DÖRT KISIT ZORUNLU — üçü yetmez. `firestore.rules` `allow list` bu
  // alanları okuyor ve kuralın SORGU KISITLARINDAN kanıtlanabilir olmasını
  // istiyor. Eksik kısıt → `Property X is undefined` → SORGUNUN TAMAMI
  // reddedilir (bu bir kusur değil, zorlama mekanizması). Ölçüldü:
  // `functions/tests/rules_clips_eksik_alan.test.js`.
  const parts: QueryConstraint[] = [
    where("v3_uploadedVia", "==", "v3"),
    where("v3_ready", "==", true),
    where("authorId", "in", ids),
    where("authorIsPrivate", "==", false),
    ...orderByForRank(rankBy),
  ];
  if (cursorDoc) parts.push(startAfter(cursorDoc));
  parts.push(limit(pageSize));
  return query(collection(db, CLIPS_COLL), ...parts);
}

/**
 * Tek bir yazarın ready cliplerini döner (profil sayfasındaki "public feed"
 * tabı için). Sahibin kendi profilinde processing clipleri de görmesi gerekir;
 * oraya bunu kullanmıyoruz.
 */
export function buildReadyClipsByAuthorQuery({
  authorId,
  pageSize = DEFAULT_CLIPS_PAGE_SIZE,
  cursorDoc = null,
  rankBy = DEFAULT_RANK_BY,
}: Omit<KlipSayfaSecenekleri, "authorIds"> & { authorId?: string | null }) {
  if (!authorId) return null;
  // EMİR #99 §4.2 — AYNI KUSUR, AYNI SÖZLEŞME (kardeş yüzey taraması kuralı:
  // bir hata sınıfı bir yüzeyde kapatılınca kardeşleri taranır; tek yer
  // düzeltmek sınıfı kapatmaz, TAŞIR).
  const parts: QueryConstraint[] = [
    where("v3_uploadedVia", "==", "v3"),
    where("v3_ready", "==", true),
    where("authorId", "==", authorId),
    where("authorIsPrivate", "==", false),
    ...orderByForRank(rankBy),
  ];
  if (cursorDoc) parts.push(startAfter(cursorDoc));
  parts.push(limit(pageSize));
  return query(collection(db, CLIPS_COLL), ...parts);
}

/**
 * EMİR #102 — PROFİL KLİP DİNLEYİCİSİ (masaüstü/mobil klip ızgarası).
 *
 * ── ÖLÇÜLEN KUSUR (emülatör, jar v1.22.0, yabancı kimlik) ────────────────
 * `ClipsDesktop:262` ve `MyClips:475` yalnız `authorId == userId` kısıtı
 * taşıyordu. `clips` list kuralı:
 *     (v3_ready == true        || authorId == request.auth.uid)
 *  && (authorIsPrivate != true || authorId == request.auth.uid)
 * Sahip olmayan için sağ operandlar FALSE; sol operandların alanları sorguda
 * kısıtlanmadığı için TANIMSIZ → `hata || false` = **sorgunun TAMAMI RED**.
 * Ölçüm: ziyaretçi → `permission-denied`; ekran sessizce "Henüz Clip Yok".
 * `onSnapshot`ta hata error-callback'e düştüğü için kullanıcı bunu ARIZA
 * değil YOKLUK olarak okuyordu (#97 yanlış-yokluk sınıfı).
 *
 * ── SAHİP / ZİYARETÇİ AYRIMI (kısıt uid'den TÜRETİLİR) ──────────────────
 * Sahip için kısıt EKLENMEZ: kendi işlenmekte olan (`v3_ready` henüz yok) ve
 * gizli kliplerini görmeye devam etmeli. Desen `UserPosts.gizlilikKisitlari`
 * ve `commentsClient.yorumHedefi` ile AYNI: prop'tan değil kimlikten türet.
 *
 * ── INDEX (yeni index EKLENMEDİ — emir §3.4) ────────────────────────────
 * Ziyaretçi kolu `v3_uploadedVia|v3_ready|authorId|authorIsPrivate|tarih↓`
 * = **mevcut idx#77**. `authorId|v3_ready|authorIsPrivate|tarih` diye bir
 * index YOK; onu gerektiren daha dar bir şekil seçilseydi sorgu
 * `failed-precondition` ile YİNE error-callback'e düşer ve kusur AYNI KILIĞA
 * girerdi. Sahip kolu `authorId|tarih↓` = mevcut idx#36.
 * ⚠️ Bedeli açıkça yazılı: ziyaretçi kolunda `v3_uploadedVia=="v3"` zorunlu
 * olduğu için V2/legacy klipler bu iki yüzeyde ziyaretçiye görünmez. Bugün
 * ziyaretçi HİÇBİR klip göremiyor (tüm sorgu reddediliyor) → kesin iyileşme,
 * ama kayıp değil **TAKAS** olarak kaydedilir.
 */
export function buildProfileClipsQuery({
  authorId,
  viewerUid = null,
  pageSize = 60,
}: {
  authorId?: string | null;
  viewerUid?: string | null;
  pageSize?: number;
}) {
  if (!authorId) return null;
  const sahip = !!viewerUid && viewerUid === authorId;
  const parts: QueryConstraint[] = [where("authorId", "==", authorId)];
  if (!sahip) {
    parts.push(where("v3_uploadedVia", "==", "v3"));
    parts.push(where("v3_ready", "==", true));
    parts.push(where("authorIsPrivate", "==", false));
  }
  parts.push(orderBy("tarih", "desc"), limit(pageSize));
  return query(collection(db, CLIPS_COLL), ...parts);
}

/**
 * 30+ yazar için ready clipleri PARÇALI (chunked) çeker: authorIds 30'luk
 * parçalara bölünür, her parça mevcut buildReadyClipsForAuthorsQuery ile
 * paralel getDocs edilir, sonuçlar id ile dedup edilip birleştirilir.
 * Firestore `in` 30-cap'i yüzünden tek sorguda kaybolan yazarları kurtarır.
 * Yeni index gerektirmez (her parça bugünkü sorgu şeklini kullanır).
 * Dönüş: merged + dedup'lı clip item dizisi (`KlipOgesi[]`).
 */
export async function fetchReadyClipsForAuthorsChunked({
  authorIds,
  perChunkPageSize = DEFAULT_CLIPS_PAGE_SIZE,
  rankBy = DEFAULT_RANK_BY,
  maxChunks = Infinity,
}: {
  authorIds?: string[] | null;
  perChunkPageSize?: number;
  rankBy?: SiralamaAlani;
  maxChunks?: number;
}) {
  const ids = Array.isArray(authorIds) ? authorIds.filter(Boolean) : [];
  if (ids.length === 0) return [];

  const chunks: string[][] = [];
  for (let i = 0; i < ids.length; i += IN_OPERATOR_MAX) {
    chunks.push(ids.slice(i, i + IN_OPERATOR_MAX));
    if (chunks.length >= maxChunks) break;
  }

  const pages = await Promise.all(
    chunks.map((chunkIds) =>
      fetchReadyClipsForAuthorsPage({
        authorIds: chunkIds,
        pageSize: perChunkPageSize,
        cursorDoc: null,
        rankBy,
      }).catch(() => ({ items: [], lastDoc: null, hasMore: false })),
    ),
  );

  const seen = new Set<string>();
  const merged: KlipOgesi[] = [];
  for (const page of pages) {
    for (const it of page.items) {
      if (it && it.id && !seen.has(it.id)) {
        seen.add(it.id);
        merged.push(it);
      }
    }
  }
  return merged;
}

/**
 * Bir sayfayı getDocs ile çeker — cursor pagination için.
 * Dönüş şekli TÜRETİLİR (`ReturnType`), JSDoc'ta elle sayılmaz.
 */
export async function fetchReadyClipsForAuthorsPage({
  authorIds,
  pageSize = DEFAULT_CLIPS_PAGE_SIZE,
  cursorDoc = null,
  rankBy = DEFAULT_RANK_BY,
}: KlipSayfaSecenekleri) {
  const q = buildReadyClipsForAuthorsQuery({ authorIds, pageSize, cursorDoc, rankBy });
  if (!q) return { items: [], lastDoc: null, hasMore: false };
  const snap = await getDocs(q);
  const docs = snap.docs;
  const items = docs.map((d) => ({ ...d.data(), id: d.id, type: "clip" }));
  const lastDoc = docs.length > 0 ? docs[docs.length - 1] : null;
  return { items, lastDoc, hasMore: docs.length === pageSize };
}
